import type { DashboardAsset } from "../../types.js";
import type {
  DashboardAssetInventoryDto,
  DashboardAssetRecordDto,
  DashboardAssetUploadRequestDto,
} from "../types/assets.api";
import {
  createAdminApiFailure,
  createAdminApiSuccess,
  normalizeAdminApiError,
} from "../../../shared/api/adminApiErrors";
import type { AdminApiResult } from "../../../shared/api/types/adminApi";
import {
  filterDashboardAssets as filterAssetViewModels,
  getDashboardAssetActionLabel,
  getDashboardAssetDeferredActionNotice,
  getDashboardAssetIcon,
  getDashboardAssetInventory,
  getDashboardAssetModalMetaIcon,
  getDashboardAssetModalPreviewLabel,
  getDashboardAssetModalSummaryLabel,
  getDashboardAssetModalTitle,
  getDashboardAssetTimestampLabel,
  getDashboardAssetTypeLabel,
} from "../../services/dashboardAssetsService";
import {
  createDashboardUploadedAsset,
  downloadDashboardAsset,
  getDashboardAssetPrimaryActionState,
  getDashboardAssetRuntimeRecord,
  getDashboardAssetStatusNotice,
  getDashboardAssetUpdateActionNotice,
  getDashboardAssetUploadAccessNotice,
  getDashboardAssetUploadModeNotice,
} from "../../services/dashboardAssetUploadService";
import {
  mapAssetInventoryToViewModel,
  mapAssetRecordToViewModel,
} from "../mappers/assetsMappers";
import { listAdminFiles, type ViewerFileRecord } from "../../../../services/viewerProjectService";

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "0 B";
  }

  const units = ["B", "KB", "MB", "GB"];
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / 1024 ** exponent;
  const formatted =
    value >= 10 || exponent === 0 ? value.toFixed(0) : value.toFixed(1);
  return `${formatted} ${units[exponent]}`;
}

function formatUpdatedAtLabel(value: string): string {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) {
    return "Recently";
  }

  const deltaMs = Date.now() - timestamp;
  const deltaMinutes = Math.max(0, Math.round(deltaMs / 60_000));
  if (deltaMinutes < 1) {
    return "Just now";
  }
  if (deltaMinutes < 60) {
    return `${deltaMinutes} min ago`;
  }

  const deltaHours = Math.round(deltaMinutes / 60);
  if (deltaHours < 24) {
    return `${deltaHours} hr ago`;
  }

  const deltaDays = Math.round(deltaHours / 24);
  return `${deltaDays} day${deltaDays === 1 ? "" : "s"} ago`;
}

function toDashboardAssetRecord(file: ViewerFileRecord): DashboardAssetRecordDto {
  const projectLabel = file.project_id?.trim()
    ? `Project ${file.project_id}`
    : "Workspace library";

  return {
    id: file.file_id,
    fileName: file.name,
    assetType: file.asset_type,
    readiness: file.readiness,
    sizeLabel: formatBytes(file.size_bytes ?? 0),
    updatedAtLabel: formatUpdatedAtLabel(file.updated_at),
    description: `Persisted through the classified file pipeline for ${projectLabel}.`,
    metadata: [
      {
        label: "Scope",
        value: file.project_id ? "Workspace library + linked project" : "Workspace library",
      },
      {
        label: "Storage",
        value: "Supabase Storage",
      },
      {
        label: "Status",
        value: file.readiness,
      },
    ],
  };
}

function buildInventoryFromRecords(
  items: ReadonlyArray<DashboardAssetRecordDto>,
): DashboardAssetInventoryDto {
  const countsByType = {
    model: 0,
    image: 0,
    pdf: 0,
    video: 0,
    cad: 0,
  } as DashboardAssetInventoryDto["countsByType"];
  const countsByReadiness = {
    staged: 0,
    processing: 0,
    ready: 0,
    failed: 0,
  } as DashboardAssetInventoryDto["countsByReadiness"];

  let totalBytes = 0;
  for (const item of items) {
    countsByType[item.assetType] += 1;
    countsByReadiness[item.readiness] += 1;
    const numericSize = Number.parseFloat(item.sizeLabel);
    if (Number.isFinite(numericSize) && item.sizeLabel.includes("KB")) {
      totalBytes += numericSize * 1024;
    } else if (Number.isFinite(numericSize) && item.sizeLabel.includes("MB")) {
      totalBytes += numericSize * 1024 ** 2;
    } else if (Number.isFinite(numericSize) && item.sizeLabel.includes("GB")) {
      totalBytes += numericSize * 1024 ** 3;
    } else if (Number.isFinite(numericSize) && item.sizeLabel.includes("B")) {
      totalBytes += numericSize;
    }
  }

  return {
    items: [...items],
    countsByType,
    countsByReadiness,
    storageUsedLabel: formatBytes(totalBytes),
    totalAssetsLabel: `${items.length} asset${items.length === 1 ? "" : "s"}`,
  };
}

function mergeAssetRecords(
  additionalAssets: ReadonlyArray<DashboardAsset>,
  persistedAssets: ReadonlyArray<DashboardAssetRecordDto>,
): DashboardAssetRecordDto[] {
  const merged = new Map<string, DashboardAssetRecordDto>();

  for (const asset of additionalAssets) {
    merged.set(asset.id, toAssetRecord(asset));
  }

  for (const asset of persistedAssets) {
    if (!merged.has(asset.id)) {
      merged.set(asset.id, asset);
    }
  }

  return [...merged.values()];
}

function toAssetRecord(asset: DashboardAsset): DashboardAssetRecordDto {
  return {
    id: asset.id,
    fileName: asset.name,
    assetType: asset.type,
    readiness: asset.readiness,
    sizeLabel: asset.sizeLabel,
    updatedAtLabel: asset.updatedAtLabel,
    description: asset.description,
    metadata: asset.metadata.map((entry) => ({ ...entry })),
  };
}

function normalizeUploadScopeRequest(
  request: DashboardAssetUploadRequestDto,
): DashboardAssetUploadRequestDto {
  const projectContext = request.projectContext ?? {
    companyId: request.companyId,
    productId: request.productId,
    entityId: request.entityId,
    entityType: request.entityType,
  };

  return {
    ...request,
    companyId: projectContext.companyId,
    productId: projectContext.productId,
    entityId: projectContext.entityId,
    entityType: projectContext.entityType,
    projectContext,
    // Assets-page uploads are workspace-scoped by default unless explicitly overridden.
    scope: request.scope ?? "workspace",
  };
}

export function getDashboardAssetInventoryData(
  additionalAssets: ReadonlyArray<DashboardAsset> = [],
): DashboardAssetInventoryDto {
  const inventory = getDashboardAssetInventory(additionalAssets);
  return {
    items: inventory.assets.map(toAssetRecord),
    countsByType: inventory.countsByType,
    countsByReadiness: inventory.countsByReadiness,
    storageUsedLabel: inventory.storageUsedLabel,
    totalAssetsLabel: inventory.totalAssetsLabel,
  };
}

export function getDashboardAssetInventoryViewModel(
  additionalAssets: ReadonlyArray<DashboardAsset> = [],
) {
  return mapAssetInventoryToViewModel(
    getDashboardAssetInventoryData(additionalAssets),
  );
}

export function mapDashboardAssetInventoryToViewModel(
  inventory: DashboardAssetInventoryDto,
) {
  return mapAssetInventoryToViewModel(inventory);
}

export async function fetchDashboardAssetInventory(
  additionalAssets: ReadonlyArray<DashboardAsset> = [],
): Promise<AdminApiResult<DashboardAssetInventoryDto>> {
  try {
    console.info("ASSETS_QUERY_PARAMS", {
      projectId: null,
      scope: "workspace-authenticated",
      optimisticAssetCount: additionalAssets.length,
    });
    const files = await listAdminFiles();
    const persistedAssets = files
      .filter((file) =>
        file.asset_type === "model" ||
        file.asset_type === "image" ||
        file.asset_type === "pdf" ||
        file.asset_type === "video" ||
        file.asset_type === "cad",
      )
      .map(toDashboardAssetRecord);

    return createAdminApiSuccess(
      buildInventoryFromRecords(
        mergeAssetRecords(additionalAssets, persistedAssets),
      ),
    );
  } catch (error) {
    console.error("ASSETS_QUERY_ERROR", {
      projectId: null,
      scope: "workspace-authenticated",
      message: error instanceof Error ? error.message : "Unable to load assets right now.",
      error,
    });
    return createAdminApiFailure(
      normalizeAdminApiError({
        code: "ASSETS_LOAD_FAILED",
        message:
          error instanceof Error
            ? error.message
            : "Unable to load assets right now.",
      }),
    );
  }
}

export function filterDashboardAssetResults(
  assets: ReadonlyArray<DashboardAsset>,
  activeTab: DashboardAsset["type"],
  searchQuery: string,
): DashboardAsset[] {
  return filterAssetViewModels(assets, activeTab, searchQuery);
}

export async function uploadDashboardAsset(
  request: DashboardAssetUploadRequestDto,
): Promise<DashboardAssetRecordDto> {
  const result = await submitDashboardAssetUpload(request);
  if (!result.ok) {
    throw new Error(result.error.message);
  }

  return result.data;
}

export async function submitDashboardAssetUpload(
  request: DashboardAssetUploadRequestDto,
): Promise<AdminApiResult<DashboardAssetRecordDto>> {
  try {
    console.info("ASSET_UPLOAD_INPUT", {
      assetType: request.assetType,
      companyId: request.companyId,
      productId: request.productId,
      entityId: request.entityId,
      entityType: request.entityType,
      scope: request.scope ?? "workspace",
      fileName: request.file.name,
      fileSize: request.file.size,
    });
    const asset = await createDashboardUploadedAsset(
      normalizeUploadScopeRequest(request),
    );
    return createAdminApiSuccess(toAssetRecord(asset));
  } catch (error) {
    console.error("ASSET_UPLOAD_ERROR", {
      assetType: request.assetType,
      companyId: request.companyId,
      productId: request.productId,
      entityId: request.entityId,
      entityType: request.entityType,
      scope: request.scope ?? "workspace",
      fileName: request.file.name,
      message:
        error instanceof Error
          ? error.message
          : "Unable to upload asset right now.",
      error,
    });
    return createAdminApiFailure(
      normalizeAdminApiError({
        code: "ASSET_UPLOAD_FAILED",
        message:
          error instanceof Error
            ? error.message
            : "Unable to upload asset right now.",
      }),
    );
  }
}

export function mapUploadedAssetToViewModel(
  asset: DashboardAssetRecordDto,
): DashboardAsset {
  return mapAssetRecordToViewModel(asset);
}

export {
  downloadDashboardAsset,
  getDashboardAssetActionLabel,
  getDashboardAssetDeferredActionNotice,
  getDashboardAssetIcon,
  getDashboardAssetModalMetaIcon,
  getDashboardAssetModalPreviewLabel,
  getDashboardAssetModalSummaryLabel,
  getDashboardAssetModalTitle,
  getDashboardAssetPrimaryActionState,
  getDashboardAssetRuntimeRecord,
  getDashboardAssetStatusNotice,
  getDashboardAssetTimestampLabel,
  getDashboardAssetTypeLabel,
  getDashboardAssetUpdateActionNotice,
  getDashboardAssetUploadAccessNotice,
  getDashboardAssetUploadModeNotice,
};
